import type { AppearanceTypes } from '@atlaskit/flag/types';
import type { Payload } from '@atlaskit/rovo-triggers/types';
import type { ForgeDoc } from '@atlassian/forge-ui-types/forge';

import { type HistoryApiProvider } from './methods/history';
import type { PermissionBridgeMethods } from './methods/permissions/usePermission';

type UserConsentState = {
	requiresUserConsent: boolean;
};

export type BridgeNavigatePayload = {
	type: 'same-tab' | 'new-tab';
	url: string;
};

/**
 * Passed as a prop to the UI Kit components that can navigate, rather than
 * through renderer-wide context, so a change in callback identity only
 * re-renders those components.
 */
export type HostNavigationProps = {
	onNavigate?: (payload: BridgeNavigatePayload) => void;
};

type BaseOpenRovoPayload = {
	prompt?: string;
	sourceAppId?: string;
	sourceEnvironmentId?: string;
	sourceInstallationId?: string;
};
type ForgeAgentPayload = BaseOpenRovoPayload & { agentKey: string; agentName: string };
type NonForgeAgentPayload = BaseOpenRovoPayload & { agentKey?: never; agentName?: string };
export type OpenRovoPayload = ForgeAgentPayload | NonForgeAgentPayload;

type RovoContextPayload = Extract<Payload, { type: `${string}-context-payload` }>;

type WithoutOpenChat<T> = T extends unknown ? Omit<T, 'openChat'> : never;

/**
 * A typed Rovo context event supplied by Forge. The host owns `openChat` and
 * always forces it to false before publishing.
 */
export type SetRovoContextPayload = WithoutOpenChat<RovoContextPayload>;

export type OpenPanelPayload = {
	onClose?: () => void;
	resource: string;
};

export interface BridgeConfig extends HistoryApiProvider, PermissionBridgeMethods {
	changeWindowTitle?: (title: string) => void;
	close?: (payload?: any) => boolean | undefined | void;
	closeFlag?: (options: Pick<FlagOptions, 'id'>) => boolean | undefined;
	closePanel?: () => boolean | void;
	emitReadyEvent?: () => void;
	getFrameDispatch?: () => ((action?: unknown) => void) | undefined;
	onClose?: (payload?: any) => boolean | undefined | void;
	onError?: (options: { error: Error }) => boolean | undefined | void;
	/**
	 * Called immediately before an accepted bridge navigation is performed.
	 * It is not called when URL validation fails or an external-link warning is
	 * cancelled.
	 */
	onNavigate?: (payload: BridgeNavigatePayload) => void;
	open?: () => boolean | undefined | void;
	openModal?: (opts: any, consentState?: UserConsentState) => boolean | undefined;
	/**
	 * Opts this host surface into opening `new-tab` navigations as a centred,
	 * popup-sized window rather than a full browser tab. Only takes effect for
	 * apps on the popup allowlist in `navigate`.
	 */
	openNewTabAsPopupWindow?: boolean;
	openPanel?: (payload: OpenPanelPayload) => boolean | void;
	openRovo?: (payload: OpenRovoPayload) => Promise<boolean | void>;
	reconcile?: (options: { forgeDoc: ForgeDoc }) => boolean | undefined | void;
	refresh?: () => boolean | undefined;
	showFlag?: (options: FlagOptions) => boolean | undefined;
	spaPush?: ((path: string, state?: any) => void) | ((url: string, forceReload?: boolean) => void);
	submit?: (payload?: any) => boolean | undefined;
}

export type FlagAppearance = Exclude<AppearanceTypes, 'normal'>;
export type FlagType = FlagAppearance;

export interface Flag {
	close: () => Promise<boolean | void>;
}

export interface FlagAction {
	onClick: () => void;
	text: string;
}

export interface FlagOptions {
	actions?: FlagAction[];
	/**
	 * When `appearance` is given, the Flag is bold. If not, it's a normal Flag.
	 */
	appearance?: FlagAppearance;
	description?: string;
	id?: number | string;
	isAutoDismiss?: boolean;
	onClose?: () => void;
	title?: string;
	/**
	 * `type` is used to determine the icon of Flag.
	 * If `appearance` is given, `type` is overriden to equal `appearance`.
	 */
	type?: FlagType;
}

// oxlint-disable-next-line eslint/no-redeclare
export const ModalNamedSizes: {
	readonly FULLSCREEN: 'fullscreen';
	readonly LARGE: 'large';
	readonly MAX: 'max';
	readonly MEDIUM: 'medium';
	readonly RESIZABLE: 'resizable';
	readonly SMALL: 'small';
	readonly XLARGE: 'xlarge';
} = {
	SMALL: 'small',
	MEDIUM: 'medium',
	LARGE: 'large',
	XLARGE: 'xlarge',
	MAX: 'max',
	FULLSCREEN: 'fullscreen',
	RESIZABLE: 'resizable',
};

export type ModalNamedSizes = (typeof ModalNamedSizes)[keyof typeof ModalNamedSizes];

export interface ModalCustomDimensions {
	height: string;
	width: string;
}

export type ModalSizes = ModalNamedSizes | ModalCustomDimensions;

export const isNamedSize = (size: ModalSizes): size is ModalNamedSizes =>
	typeof size === 'string' && Object.values(ModalNamedSizes).includes(size as ModalNamedSizes);
export const isCustomDimensions = (size: ModalSizes): size is ModalCustomDimensions =>
	typeof size === 'object';

export interface ModalOptions {
	context?: any;
	icon?: string;
	onClose?: (payload: any) => any;
	resource?: string | null;
	size?: ModalSizes;
	title?: string;
}
